import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalSuccess, swalError } from '../utils/alert';
import { maskCurrency, parseCurrency, formatInitialAmount } from '../utils/currency';
import QuickCreateModal from './QuickCreateModal';
import './RevenueForm.css';
import './RevenueDesktop.css';
import './QuickCreateModal.css';
import { toLocalDateInput } from '@sf/shared';

function RevenueForm({ revenue, onSuccess, initialValues }) {
  const [formData, setFormData] = useState(() =>
    revenue
      ? { ...revenue, amount: formatInitialAmount(revenue.amount) }
      : {
          description: '',
          category_id: '',
          amount: '',
          due_date: '',
          payment_date: '',
          bank_account_id: '',
          customer_id: initialValues?.customer_id || '',
          payment_method: 'PIX',
          status: 'Pendente',
          observations: '',
          attachments: [],
          installments: 1,
          is_fixed: false,
          is_simulation: false,
        }
  );
  const [amountType, setAmountType] = useState('total');
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showObservations, setShowObservations] = useState(false);
  const [quickCreate, setQuickCreate] = useState(null); // null | 'category' | 'bank' | 'customer'
  const paymentDateRef = useRef(null);
  const width = useWindowWidth();
  const isMobile = width < 768;

  const paymentMethods = [
    { id: 'PIX', label: 'PIX', icon: '⚡' },
    { id: 'Transferência', label: 'TED/DOC', icon: '🔄' },
    { id: 'Dinheiro', label: 'Dinheiro', icon: '💵' },
    { id: 'Cheque', label: 'Cheque', icon: '📝' },
  ];

  const statusOptions = [
    { id: 'Pendente', label: 'Pendente', color: 'warning' },
    { id: 'Recebida', label: 'Recebida', color: 'success' },
  ];

  useEffect(() => {
    api.get('/categories?type=Receita').then(res => setCategories(res.data));
    api.get('/banks').then(res => setBanks(res.data));
    api.get('/customers').then(res => setCustomers(res.data.data));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'amount') {
      setFormData({ ...formData, amount: maskCurrency(value) });
      return;
    }
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handlePaymentMethodClick = (method) => {
    setFormData({ ...formData, payment_method: method });
  };

  const handleStatusClick = (status) => {
    const updates = { status };
    if (status === 'Recebida' && !formData.payment_date) {
      updates.payment_date = toLocalDateInput(new Date());
    }
    setFormData({ ...formData, ...updates });
    if (status === 'Recebida') {
      setTimeout(() => paymentDateRef.current?.focus(), 80);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const method = revenue ? 'put' : 'post';
    const url = revenue ? `/revenues/${revenue.id}` : '/revenues';

    const qty = parseInt(formData.installments, 10) || 1;
    const rawAmount = parseCurrency(formData.amount);
    const totalAmount = amountType === 'unit' ? rawAmount * qty : rawAmount;

    api[method](url, { ...formData, amount: totalAmount })
      .then(() => {
        swalSuccess('Receita salva com sucesso!');
        onSuccess();
      })
      .catch(error => {
        console.error('Houve um erro ao salvar a receita!', error);
        swalError('Erro ao salvar receita.');
      });
  };

  const handleQuickCreated = (type, record) => {
    if (type === 'category') {
      setCategories(prev => [...prev, record]);
      setFormData(prev => ({ ...prev, category_id: String(record.id) }));
    } else if (type === 'bank') {
      setBanks(prev => [...prev, record]);
      setFormData(prev => ({ ...prev, bank_account_id: String(record.id) }));
    } else if (type === 'customer') {
      setCustomers(prev => [...prev, record]);
      setFormData(prev => ({ ...prev, customer_id: String(record.id) }));
    }
    setQuickCreate(null);
  };

  const formatCurrency = (val) =>
    Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const qty = parseInt(formData.installments, 10) || 1;
  const rawAmount = parseCurrency(formData.amount);
  const isMultiple = qty > 1 && !revenue;

  const previewText = isMultiple && rawAmount > 0
    ? amountType === 'total'
      ? `${qty}× R$ ${formatCurrency(rawAmount / qty)} por parcela`
      : `Total: R$ ${formatCurrency(rawAmount * qty)} · ${qty}× R$ ${formatCurrency(rawAmount)}`
    : null;

  const renderAmountTypeSelector = () => (
    isMultiple ? (
      <div className="amt-toggle-wrap">
        <span className="amt-toggle-label">Este valor é o…</span>
        <div className="amt-toggle-track">
          <div className={`amt-toggle-pill amt-toggle-pill-revenue${amountType === 'unit' ? ' slide-unit' : ''}`} />
          <button
            type="button"
            className={`amt-toggle-option${amountType === 'total' ? ' active' : ''}`}
            onClick={() => setAmountType('total')}
          >
            <span className="amt-toggle-icon">÷</span>
            <span className="amt-toggle-text-group">
              <span className="amt-toggle-title">Valor Total</span>
              <span className="amt-toggle-desc">dividido em {qty} parcelas</span>
            </span>
          </button>
          <button
            type="button"
            className={`amt-toggle-option${amountType === 'unit' ? ' active' : ''}`}
            onClick={() => setAmountType('unit')}
          >
            <span className="amt-toggle-icon">∑</span>
            <span className="amt-toggle-text-group">
              <span className="amt-toggle-title">Por Parcela</span>
              <span className="amt-toggle-desc">cada parcela vale este valor</span>
            </span>
          </button>
        </div>
        {previewText && (
          <div className="amt-preview amt-preview-revenue">{previewText}</div>
        )}
      </div>
    ) : null
  );

  // ════════════════════════════════════
  // MOBILE FORM — não alterado
  // ════════════════════════════════════
  const renderMobileForm = () => (
    <form onSubmit={handleSubmit} className="revenue-form-mobile" autoComplete="off">
      {/* Forma de Pagamento - Grid 2x2 */}
      <div className="form-section">
        <label className="section-label">Forma de Recebimento</label>
        <div className="payment-methods-grid">
          {paymentMethods.map(method => (
            <button
              type="button"
              key={method.id}
              className={`payment-method-btn ${formData.payment_method === method.id ? 'active' : ''}`}
              onClick={() => handlePaymentMethodClick(method.id)}
            >
              <span className="payment-icon">{method.icon}</span>
              <span className="payment-label">{method.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Valor - Campo grande e destacado (verde para receita) */}
      <div className="form-section amount-section">
        <label className="section-label">Valor</label>
        <div className="amount-input-wrapper">
          <span className="currency-symbol">R$</span>
          <input
            type="text"
            inputMode="numeric"
            className="amount-input"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder="0,00"
            autoComplete="off"
            required
          />
        </div>
      </div>

      {/* Descrição */}
      <div className="form-section">
        <label className="section-label">Descrição</label>
        <input
          type="text"
          className="form-control form-control-lg"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Ex: Venda de produto, Serviço..."
          autoComplete="off"
          required
        />
      </div>

      {/* Data e Parcelas lado a lado */}
      <div className="form-section">
        <div className="row g-2">
          <div className="col-6">
            <label className="section-label">Vencimento</label>
            <input
              type="date"
              className="form-control"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-6">
            <label className="section-label">Parcelas</label>
            <input
              type="number"
              className="form-control"
              name="installments"
              value={formData.installments}
              onChange={handleChange}
              min="1"
              disabled={!!revenue}
            />
          </div>
        </div>
      </div>

      {/* Valor Total vs Unitário */}
      {isMultiple && (
        <div className="form-section">
          {renderAmountTypeSelector()}
        </div>
      )}

      {/* Receita Fixa */}
      <div className="form-section">
        <label className="is-fixed-label">
          <input
            type="checkbox"
            name="is_fixed"
            checked={!!formData.is_fixed}
            onChange={handleChange}
            className="is-fixed-checkbox"
          />
          Receita fixa (recorrente todo mês)
        </label>
        <label className="is-simulation-label">
          <input
            type="checkbox"
            name="is_simulation"
            checked={!!formData.is_simulation}
            onChange={handleChange}
            className="is-simulation-checkbox"
          />
          🧪 Lançamento simulado (análise temporária)
        </label>
      </div>

      {/* Categoria */}
      <div className="form-section">
        <div className="qc-label-row">
          <label className="section-label">Categoria</label>
          <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('category')}>+ Nova</button>
        </div>
        <select
          className="form-select form-select-lg"
          name="category_id"
          value={formData.category_id}
          onChange={handleChange}
          required
        >
          <option value="">Selecionar categoria</option>
          {categories.map(category => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </select>
      </div>

      {/* Conta Bancária */}
      <div className="form-section">
        <div className="qc-label-row">
          <label className="section-label">Conta Bancária</label>
          <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('bank')}>+ Nova</button>
        </div>
        <select
          className="form-select form-select-lg"
          name="bank_account_id"
          value={formData.bank_account_id}
          onChange={handleChange}
          required
        >
          <option value="">Selecionar conta</option>
          {banks.map(bank => (
            <option key={bank.id} value={bank.id}>{bank.name}</option>
          ))}
        </select>
      </div>

      {/* Status - Botões */}
      <div className="form-section">
        <label className="section-label">Status</label>
        <div className="status-buttons">
          {statusOptions.map(status => (
            <button
              type="button"
              key={status.id}
              className={`status-btn ${status.color} ${formData.status === status.id ? 'active' : ''}`}
              onClick={() => handleStatusClick(status.id)}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Data de Recebimento */}
      {formData.status === 'Recebida' && (
        <div className="form-section payment-date-highlight-rv">
          <label className="section-label">Data de Recebimento</label>
          <input
            ref={paymentDateRef}
            type="date"
            className="form-control"
            name="payment_date"
            value={formData.payment_date || ''}
            onChange={handleChange}
          />
        </div>
      )}

      {/* Cliente */}
      <div className="form-section">
        <div className="qc-label-row">
          <label className="section-label">Cliente (Opcional)</label>
          <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('customer')}>+ Novo</button>
        </div>
        <select
          className="form-select"
          name="customer_id"
          value={formData.customer_id}
          onChange={handleChange}
        >
          <option value="">Nenhum</option>
          {customers.map(customer => (
            <option key={customer.id} value={customer.id}>{customer.name}</option>
          ))}
        </select>
      </div>

      {/* Observações - Expansível */}
      <div className="form-section">
        <button
          type="button"
          className="expand-btn"
          onClick={() => setShowObservations(!showObservations)}
        >
          <span>Observações</span>
          <span className="expand-icon">{showObservations ? '−' : '+'}</span>
        </button>
        {showObservations && (
          <textarea
            className="form-control mt-2"
            name="observations"
            value={formData.observations}
            onChange={handleChange}
            rows="3"
            placeholder="Adicione observações..."
          />
        )}
      </div>

      {/* Botão Salvar Fixo */}
      <div className="mobile-form-footer">
        <button type="submit" className="btn btn-success btn-lg w-100">
          Salvar Receita
        </button>
      </div>
    </form>
  );

  // ════════════════════════════════════
  // DESKTOP FORM — redesenhado
  // ════════════════════════════════════
  const renderDesktopForm = () => (
    <form onSubmit={handleSubmit} className="rv-form-desktop">

      {/* Forma de recebimento */}
      <div className="rv-form-field-full">
        <label className="rv-form-label">Forma de Recebimento</label>
        <div className="rv-payment-grid">
          {paymentMethods.map(method => (
            <button
              type="button"
              key={method.id}
              className={`rv-payment-btn ${formData.payment_method === method.id ? 'active' : ''}`}
              onClick={() => handlePaymentMethodClick(method.id)}
            >
              <span className="rv-pay-icon">{method.icon}</span>
              <span>{method.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Descrição + Status */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="description">Descrição</label>
          <input
            type="text"
            className="rv-form-input"
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Ex: Venda de produto, Serviço..."
            required
          />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label">Status</label>
          <div className="rv-status-group">
            {statusOptions.map(s => (
              <button
                type="button"
                key={s.id}
                className={`rv-status-toggle ${formData.status === s.id ? `active-${s.color}` : ''}`}
                onClick={() => handleStatusClick(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Valor + Vencimento + Parcelas */}
      <div className="rv-form-grid-3">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="amount">Valor (R$)</label>
          <input
            type="text"
            inputMode="numeric"
            className="rv-form-input"
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
          />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="due_date">Vencimento</label>
          <input
            type="date"
            className="rv-form-input"
            id="due_date"
            name="due_date"
            value={formData.due_date}
            onChange={handleChange}
            required
          />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="installments">Parcelas</label>
          <input
            type="number"
            className="rv-form-input"
            id="installments"
            name="installments"
            value={formData.installments}
            onChange={handleChange}
            min="1"
            disabled={!!revenue}
          />
        </div>
      </div>

      {/* Valor Total vs Unitário */}
      {isMultiple && (
        <div className="rv-form-field-full">
          {renderAmountTypeSelector()}
        </div>
      )}

      {/* Receita Fixa */}
      <div className="rv-form-field-full">
        <label className="is-fixed-label">
          <input
            type="checkbox"
            name="is_fixed"
            checked={!!formData.is_fixed}
            onChange={handleChange}
            className="is-fixed-checkbox"
          />
          Receita fixa (recorrente todo mês)
        </label>
        <label className="is-simulation-label">
          <input
            type="checkbox"
            name="is_simulation"
            checked={!!formData.is_simulation}
            onChange={handleChange}
            className="is-simulation-checkbox"
          />
          🧪 Lançamento simulado
        </label>
      </div>

      {/* Categoria + Conta Bancária */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <div className="qc-label-row">
            <label className="rv-form-label" htmlFor="category_id">Categoria</label>
            <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('category')}>+ Nova</button>
          </div>
          <select className="rv-form-select" id="category_id" name="category_id" value={formData.category_id} onChange={handleChange} required>
            <option value="">Selecionar Categoria</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="rv-form-field">
          <div className="qc-label-row">
            <label className="rv-form-label" htmlFor="bank_account_id">Conta Bancária</label>
            <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('bank')}>+ Nova</button>
          </div>
          <select className="rv-form-select" id="bank_account_id" name="bank_account_id" value={formData.bank_account_id} onChange={handleChange} required>
            <option value="">Selecionar Conta</option>
            {banks.map(b => <option key={b.id} value={b.id}>{b.name} - {b.account_number}</option>)}
          </select>
        </div>
      </div>

      {/* Cliente + Data de Recebimento */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <div className="qc-label-row">
            <label className="rv-form-label" htmlFor="customer_id">Cliente (Opcional)</label>
            <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('customer')}>+ Novo</button>
          </div>
          <select className="rv-form-select" id="customer_id" name="customer_id" value={formData.customer_id} onChange={handleChange}>
            <option value="">Nenhum</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className={`rv-form-field${formData.status === 'Recebida' ? ' payment-date-highlight-rv' : ''}`}>
          <label className="rv-form-label" htmlFor="payment_date">
            Data de Recebimento{formData.status !== 'Recebida' ? ' (Opcional)' : ''}
          </label>
          <input
            ref={paymentDateRef}
            type="date"
            className="rv-form-input"
            id="payment_date"
            name="payment_date"
            value={formData.payment_date || ''}
            onChange={handleChange}
          />
        </div>
        <div></div>
      </div>

      {/* Observações */}
      <div className="rv-form-field-full">
        <label className="rv-form-label" htmlFor="observations">Observações (Opcional)</label>
        <textarea
          className="rv-form-textarea"
          id="observations"
          name="observations"
          value={formData.observations}
          onChange={handleChange}
          placeholder="Adicione observações..."
        />
      </div>

      {/* Salvar */}
      <div className="rv-form-footer">
        <button type="submit" className="rv-btn-submit">Salvar Receita</button>
      </div>
    </form>
  );

  return (
    <>
      {isMobile ? renderMobileForm() : renderDesktopForm()}
      {quickCreate && (
        <QuickCreateModal
          type={quickCreate}
          context="Receita"
          onCreated={(record) => handleQuickCreated(quickCreate, record)}
          onClose={() => setQuickCreate(null)}
        />
      )}
    </>
  );
}

export default RevenueForm;
